import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { criarTema, ModoTema } from "../theme";

const CHAVE_TEMA = "xc-helpdesk-tema";

interface TemaContextValor {
  modo: ModoTema;
  alternarModo: () => void;
}

const TemaContext = createContext<TemaContextValor>({
  modo: "claro",
  alternarModo: () => {},
});

const lerModoSalvo = (): ModoTema => {
  try {
    return localStorage.getItem(CHAVE_TEMA) === "escuro" ? "escuro" : "claro";
  } catch {
    return "claro";
  }
};

// Tema claro/escuro da área logada; o modo escolhido fica salvo no navegador
export const TemaProvider = ({ children }: { children: ReactNode }) => {
  const [modo, setModo] = useState<ModoTema>(lerModoSalvo);

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_TEMA, modo);
    } catch {
      // Sem acesso ao localStorage o modo vale só para esta sessão
    }

    // Marca o <html> para as regras do modo escuro sobre o Bootstrap/Hyper (styles/tema-escuro.css)
    document.documentElement.setAttribute("data-tema", modo);
    return () => document.documentElement.removeAttribute("data-tema");
  }, [modo]);

  const tema = useMemo(() => criarTema(modo), [modo]);

  const valor = useMemo(
    () => ({
      modo,
      alternarModo: () => setModo((atual) => (atual === "escuro" ? "claro" : "escuro")),
    }),
    [modo]
  );

  return (
    <TemaContext.Provider value={valor}>
      <ThemeProvider theme={tema}>{children}</ThemeProvider>
    </TemaContext.Provider>
  );
};

export const useTema = () => useContext(TemaContext);
